import React, { memo } from 'react';
import { Button, Flex, Modal } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { HeaderCell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	CancelButton,
	SaveButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons.tsx';
import {
	BodyRow,
	HeaderRow,
	DefaultTable,
} from '@dealstack/frontend/shared/components/Table/DefaultTable/DefaultTable.tsx';
import {
	AlertDescription,
	AlertTitle,
	Alert,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { useManageCustomDetailsDefinitionsModalController } from './useManageCustomDetailsDefinitionsModalController.ts';
import { CustomDetailDefinitionRow } from './CustomDetailDefinitionRow/CustomDetailDefinitionRow.tsx';
import {
	ManageCustomDetailDefinitionField,
	ManageCustomDetailsDefinitionsForm,
} from '@dealstack/frontend/admin/pages/Manage/common/ManageCustomDetailsDefinitionsModal/types.ts';
import { SetOptional } from 'type-fest';

interface Props {
	opened: boolean;
	initialCustomDefinitions: SetOptional<ManageCustomDetailDefinitionField, 'entityType'>[];
	onClose: () => void;
	onSubmit: (formValues: ManageCustomDetailsDefinitionsForm) => void;
	hideEntityTypeInput?: boolean;
}

const ManageCustomDetailsDefinitionsModal = ({
	opened,
	initialCustomDefinitions,
	onClose,
	onSubmit,
	hideEntityTypeInput,
}: Props) => {
	const { manageCustomDetailsDefinitionsForm, fields, onRemoveClicked, onAddNewClicked } =
		useManageCustomDetailsDefinitionsModalController(initialCustomDefinitions);

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Manage custom details definitions for template"
			size="80vw"
			centered
		>
			<FormProvider {...manageCustomDetailsDefinitionsForm}>
				<Alert icon="information-filled" state="warning" size="m">
					<AlertTitle>WARNING</AlertTitle>
					<AlertDescription>
						This won't affect already generated documents. However, subsequent effects
						can be applied to all MIPs using this template:
					</AlertDescription>
					<AlertDescription>
						1. Editing name or instructions may cause discrepancies between custom
						detail and already collected data.
					</AlertDescription>
					<AlertDescription>
						2. Deleting a definition will delete the custom details created from the
						definition, along with the investor's responses. Also, note that smart
						fields using deleted definitions will be invalid and require your attention.
					</AlertDescription>
					<AlertDescription>
						3. Creating a definition will create custom details, so all investors in
						each affected MIP will be prompted to respond during the next investor flow.
					</AlertDescription>
				</Alert>

				<DefaultTable maxSize withEditableRows>
					<HeaderRow>
						<HeaderCell customSize="1fr">Name</HeaderCell>
						<HeaderCell customSize="1fr">Instructions</HeaderCell>
						{hideEntityTypeInput ? null : (
							<HeaderCell customSize="1fr">Entity type</HeaderCell>
						)}
						<HeaderCell size={2.1} />
					</HeaderRow>
					<BodyRow>
						{fields.map((field, index) => (
							<CustomDetailDefinitionRow
								key={field.id ?? field.localId}
								onRemoveClicked={() => {
									onRemoveClicked(index);
								}}
								index={index}
								hideEntityTypeInput={hideEntityTypeInput}
							/>
						))}
					</BodyRow>
				</DefaultTable>

				<Button
					variant="ghost"
					leftSection={
						<Icon variant="add-circle" size="lg" style={{ marginRight: '0.5rem' }} />
					}
					onClick={onAddNewClicked}
					mt="1rem"
				>
					Add new
				</Button>

				<Flex mt="2rem" justify="flex-end" gap="1rem">
					<CancelButton onClick={onClose} />

					<SaveButton
						onClick={manageCustomDetailsDefinitionsForm.handleSubmit(onSubmit)}
					/>
				</Flex>
			</FormProvider>
		</Modal>
	);
};
const _ManageCustomDetailsDefinitionsModalExport = memo(ManageCustomDetailsDefinitionsModal);

export { _ManageCustomDetailsDefinitionsModalExport as ManageCustomDetailsDefinitionsModal };
